import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { UserPlus, UserMinus, ShieldBan, Loader2, Sparkles, MessageCircle, Heart, Pencil, Move } from 'lucide-react';
import AdminBadge from '../AdminBadge';
import ModeratorBadge from '../ModeratorBadge';
import { defaultAvatar } from '../../lib/avatarDefaults';

/* Floating hearts animation, shown on hover over the Likes stat */
const FloatingHearts = ({ count }) => {
  const [hearts, setHearts] = useState([]);
  React.useEffect(() => {
    const arr = Array.from({ length: count }, (_, i) => ({
      id: i,
      x: 30 + Math.random() * 40,
      delay: Math.random() * 2,
      size: 10 + Math.random() * 8,
    }));
    setHearts(arr);
  }, [count]);
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {hearts.map(h => (
        <motion.div key={h.id}
          initial={{ y: 0, opacity: 1 }}
          animate={{ y: -60, opacity: 0 }}
          transition={{ duration: 2.5, delay: h.delay, repeat: Infinity, repeatDelay: 1 }}
          className="absolute text-red-400"
          style={{ left: `${h.x}%`, bottom: 0, fontSize: h.size }}>
          <Heart className="fill-current" style={{ width: h.size, height: h.size }} />
        </motion.div>
      ))}
    </div>
  );
};

/**
 * The identity card — avatar, name, bio, stats, and social actions.
 * Mandatory on every eligible profile; only its position is user-controlled.
 */
const IdentityCardWidget = ({
  profileData, myProfile, isOwn, followLoading,
  onFollow, onMessage, onBlock, onUpgrade,
  editMode, onToggleEdit,
}) => {
  const [showHearts, setShowHearts] = useState(false);

  return (
    <div className="group relative inline-flex max-w-fit mx-auto backdrop-blur-xl bg-white/3 border border-white/8 rounded-2xl p-5 sm:p-6 shadow-2xl" data-testid="profile-card">
      {/* Edit entry point lives on the card so it is always in view. Touch
          screens have no hover, so it is always visible there; with a mouse
          it fades in on hover or keyboard focus. While editing it becomes a
          persistent "Done" pill. Only passed in for the owner of an eligible profile. */}
      {onToggleEdit && (
        editMode ? (
          <button
            onClick={onToggleEdit}
            className="absolute top-2 right-2 min-h-[44px] px-4 rounded-full text-xs font-medium bg-primary text-white shadow-lg z-20 flex items-center gap-1.5"
            data-testid="edit-layout-btn"
          >
            <Move className="w-3.5 h-3.5" aria-hidden="true" /> Done
          </button>
        ) : (
          <button
            onClick={onToggleEdit}
            className="absolute top-1 right-1 min-h-[44px] min-w-[44px] flex items-center justify-center z-20 transition-opacity duration-200 [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100 focus-visible:!opacity-100"
            style={{ filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.6))' }}
            aria-label="Edit profile layout"
            data-testid="edit-layout-btn"
          >
            <Pencil className="w-4 h-4 text-white" aria-hidden="true" />
          </button>
        )
      )}
      <div className="flex items-start gap-5 flex-col sm:flex-row">
        {/* Avatar — left */}
        <img
          src={profileData.avatar_url || defaultAvatar(profileData.gender)}
          alt={profileData.display_name}
          className="w-20 h-20 sm:w-24 sm:h-24 rounded-full object-cover border-2 border-white/30 shadow-lg flex-shrink-0"
          data-testid="profile-avatar"
        />

        {/* User info — right */}
        <div className={`flex-1 min-w-0 w-full ${onToggleEdit && !editMode ? "sm:pr-8" : ""}`}>
          {/* Name + Premium star (hidden for admins) */}
          <h1 className="font-heading text-xl sm:text-2xl font-bold text-white flex items-center gap-2 flex-wrap" data-testid="profile-display-name">
            {profileData.display_name}
            {profileData.is_premium && !profileData.is_admin && (
              <Sparkles className="w-4 h-4 text-yellow-300 fill-yellow-300/30" data-testid="profile-premium-badge" />
            )}
            <AdminBadge show={profileData.is_admin} light />
            <ModeratorBadge show={!profileData.is_admin && profileData.is_moderator} light />
          </h1>
          <p className="text-white/60 text-xs mb-2" data-testid="profile-username">@{profileData.display_name}</p>

          {/* Bio */}
          {profileData.bio && (
            <p className="text-white/80 text-sm leading-snug mb-3" data-testid="profile-bio">
              {profileData.bio}
            </p>
          )}

          {/* Stats row */}
          <div className="flex items-center gap-4 sm:gap-6 mb-3 text-white">
            {[
              { label: 'Posts', value: profileData.stats.posts },
              { label: 'Followers', value: profileData.stats.followers },
              { label: 'Following', value: profileData.stats.following },
              { label: 'Likes', value: profileData.stats.likes_received, hasHearts: true },
            ].map(({ label, value, hasHearts }) => (
              <div key={label} className="relative"
                onMouseEnter={() => hasHearts && setShowHearts(true)}
                onMouseLeave={() => hasHearts && setShowHearts(false)}
                data-testid={`stat-${label.toLowerCase()}`}>
                <p className="text-base sm:text-lg font-bold leading-none">{value}</p>
                <p className="text-[10px] sm:text-xs text-white/60 mt-0.5">{label}</p>
                {hasHearts && showHearts && <FloatingHearts count={8} />}
              </div>
            ))}
          </div>

          {/* Action buttons (below stats, inside card) */}
          {!isOwn && (
            <div className="flex items-center gap-3 flex-wrap">
              <button onClick={onFollow} disabled={followLoading}
                className={`px-5 py-2 rounded-full text-xs font-medium transition-all flex items-center justify-center gap-1.5 ${
                  profileData.is_following
                    ? 'bg-white/20 text-white hover:bg-white/30'
                    : 'bg-white text-primary hover:bg-white/90'
                } disabled:opacity-50`}
                data-testid="follow-btn">
                {followLoading ? <Loader2 className="w-4 h-4 animate-spin" /> :
                  profileData.is_following ? <><UserMinus className="w-3.5 h-3.5" /> Following</> : <><UserPlus className="w-3.5 h-3.5" /> Follow</>}
              </button>
              <button
                onClick={onMessage}
                className={`px-5 py-2 rounded-full text-xs font-medium transition-all flex items-center justify-center gap-1.5 ${
                  myProfile?.is_premium
                    ? 'bg-white/20 text-white hover:bg-white/30'
                    : 'bg-white/10 text-white/70 hover:bg-white/20'
                }`}
                title={myProfile?.is_premium ? 'Send a message' : 'Upgrade to Sirati+ to send messages'}
                data-testid="message-btn">
                <MessageCircle className="w-3.5 h-3.5" />
                Message
                {!myProfile?.is_premium && <Sparkles className="w-3 h-3 text-yellow-300" />}
              </button>
              {!profileData.is_admin && !profileData.is_moderator && (
                <button onClick={onBlock}
                  className="text-xs text-white/50 hover:text-red-300 transition-colors flex items-center gap-1"
                  data-testid="block-btn">
                  <ShieldBan className="w-3 h-3" /> Block
                </button>
              )}
            </div>
          )}

          {/* Upgrade CTA — own profile, non-premium */}
          {isOwn && !profileData.is_premium && (
            <button onClick={onUpgrade}
              className="px-5 py-2 rounded-full text-xs font-medium bg-gradient-to-r from-primary to-primary/80 text-white hover:opacity-90 transition-all flex items-center gap-1.5 shadow-lg"
              data-testid="upgrade-premium-cta">
              <Sparkles className="w-3.5 h-3.5" /> Upgrade to Sirati+
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default IdentityCardWidget;
